"use client";

import * as React from "react";
import { isCalendarDateBeforeToday } from "@/lib/calendar-date-availability";
import { fr, enUS, nl } from "date-fns/locale";
import { format, isSameDay, isSameMonth, isWithinInterval, getDay } from "date-fns";
import { cn } from "@/lib/utils";
import { DayPicker } from "react-day-picker";
import { Button } from "@/components/atoms/buttons";
import { ChevronLeft, ChevronRight } from "lucide-react";

export type DateRange = {
  from: Date | undefined;
  to?: Date | undefined;
};

const isHoliday = (disableHolidayDates: boolean, date: Date) => {
  if (!disableHolidayDates) return false;

  const monthDay = `${date.getMonth()}-${date.getDate()}`;

  return ["0-1", "3-21", "4-1", "4-29", "5-9", "11-25"].includes(monthDay);
};

interface CalendarProps extends Omit<React.ComponentProps<typeof DayPicker>, "mode" | "onSelect" | "selected"> {
  onClose?: () => void;
  title: string;
  isWeekendDisabled?: boolean;
  onReset?: () => void;
  mode?: "default" | "range";
  setLocale?: "French" | "English" | "Dutch";
  disabled?: (date: Date) => boolean;
  selected?: Date | DateRange | undefined;
  disableHolidays?: boolean;
  onSelect?:
    | ((
        date: Date | undefined,
        selectedDate: Date,
        modifiers: Record<string, boolean>,
        e: React.MouseEvent<Element> | React.KeyboardEvent<Element>
      ) => void)
    | ((
        range: DateRange | undefined,
        selectedDate: Date,
        modifiers: Record<string, boolean>,
        e: React.MouseEvent<Element> | React.KeyboardEvent<Element>
      ) => void);
}

function Calendar({
  title,
  onReset,
  setLocale: locale = "French",
  mode = "default",
  selected,
  onSelect,
  onClose,
  disabled,
  isWeekendDisabled = false,
  disableHolidays = false,
}: CalendarProps) {
  const dateLocale = React.useMemo(() => {
    switch (locale) {
      case "English":
        return enUS;
      case "Dutch":
        return nl;
      case "French":
      default:
        return fr;
    }
  }, [locale]);

  const daysOfWeek = React.useMemo(() => {
    switch (locale) {
      case "English":
        return ["Mon.", "Tue.", "Wed.", "Thu.", "Fri.", "Sat.", "Sun."];
      case "Dutch":
        return ["Ma.", "Di.", "Wo.", "Do.", "Vr.", "Za.", "Zo."];
      case "French":
      default:
        return ["Lun.", "Mar.", "Mer.", "Jeu.", "Ven.", "Sam.", "Dim."];
    }
  }, [locale]);

  const initialMonth = React.useMemo(() => {
    const today = new Date();

    if (mode === "range" && selected && typeof selected === "object" && "from" in selected && selected.from) {
      return new Date(selected.from.getFullYear(), selected.from.getMonth(), 1);
    }

    if (selected instanceof Date) {
      return new Date(selected.getFullYear(), selected.getMonth(), 1);
    }

    // Fallback to the month with the first available (non-disabled, non-weekend) date
    const startMonth = new Date(today.getFullYear(), today.getMonth(), 1);

    for (let i = 0; i < 12; i++) {
      const month = new Date(startMonth.getFullYear(), startMonth.getMonth() + i, 1);
      const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();

      for (let d = 1; d <= daysInMonth; d++) {
        const date = new Date(month.getFullYear(), month.getMonth(), d);

        const isPast = isCalendarDateBeforeToday(date, today);
        const isDisabled = disabled?.(date) ?? false;
        const isWeekend = isWeekendDisabled && (date.getDay() === 0 || date.getDay() === 6);

        if (!isPast && !isDisabled && !isWeekend) {
          return month;
        }
      }
    }

    return startMonth;
  }, [selected, mode, disabled, isWeekendDisabled]);

  const [month, setMonth] = React.useState(initialMonth);

  const handleReset = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const defaultTomorrow = new Date(today);
    defaultTomorrow.setDate(today.getDate() + 1);

    let resetDate: Date | null = defaultTomorrow;

    if (disabled || isWeekendDisabled) {
      const maxDate = new Date(today);
      maxDate.setMonth(maxDate.getMonth() + 6);

      for (let d = new Date(defaultTomorrow); d <= maxDate; d.setDate(d.getDate() + 1)) {
        const date = new Date(d);

        const isDisabledDate = disabled?.(date) ?? false;
        const isWeekend = isWeekendDisabled && (date.getDay() === 0 || date.getDay() === 6);

        if (!isDisabledDate && !isWeekend) {
          resetDate = date;
          break;
        }
      }
    }

    if (!resetDate) return;

    setMonth(resetDate);

    if (onReset) {
      onReset();
      return;
    }

    const syntheticEvent = {
      preventDefault: () => {},
      target: document.createElement("div"),
    } as unknown as React.MouseEvent<Element>;
    const modifiers = {};

    if (mode === "range" && onSelect) {
      (
        onSelect as (
          range: DateRange | undefined,
          selectedDate: Date,
          modifiers: Record<string, boolean>,
          e: React.MouseEvent<Element> | React.KeyboardEvent<Element>
        ) => void
      )({ from: resetDate, to: resetDate }, resetDate, modifiers, syntheticEvent);
    } else if (onSelect) {
      (
        onSelect as (
          date: Date | undefined,
          selectedDate: Date,
          modifiers: Record<string, boolean>,
          e: React.MouseEvent<Element> | React.KeyboardEvent<Element>
        ) => void
      )(resetDate, resetDate, modifiers, syntheticEvent);
    }
  };

  const calendarDays = React.useMemo(() => {
    const firstDayOfMonth = new Date(month.getFullYear(), month.getMonth(), 1);
    const lastDayOfMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0);

    let firstWeekdayOfMonth = getDay(firstDayOfMonth); // 0 = Sunday, 1 = Monday, etc.
    firstWeekdayOfMonth = firstWeekdayOfMonth === 0 ? 6 : firstWeekdayOfMonth - 1; // Convert to 0 = Monday

    // Get days from previous month to fill in the first week
    const daysFromPrevMonth = firstWeekdayOfMonth;
    const prevMonthDays = [];
    for (let i = daysFromPrevMonth - 1; i >= 0; i--) {
      const prevDate = new Date(firstDayOfMonth);
      prevDate.setDate(prevDate.getDate() - (i + 1));
      prevMonthDays.push(prevDate);
    }

    // Get days from current month
    const currentMonthDays = [];
    for (let i = 1; i <= lastDayOfMonth.getDate(); i++) {
      const currentDate = new Date(month.getFullYear(), month.getMonth(), i);
      currentMonthDays.push(currentDate);
    }

    // Get days from next month to fill out the last week
    const totalDaysSoFar = prevMonthDays.length + currentMonthDays.length;
    const daysNeeded = Math.ceil(totalDaysSoFar / 7) * 7 - totalDaysSoFar;
    const nextMonthDays = [];
    for (let i = 1; i <= daysNeeded; i++) {
      const nextDate = new Date(lastDayOfMonth);
      nextDate.setDate(nextDate.getDate() + i);
      nextMonthDays.push(nextDate);
    }

    // Combine all days and split into weeks
    const allDays = [...prevMonthDays, ...currentMonthDays, ...nextMonthDays];
    const weeks = [];
    for (let i = 0; i < allDays.length; i += 7) {
      weeks.push(allDays.slice(i, i + 7));
    }

    return weeks;
  }, [month]);

  const handleDateClick = (day: Date) => {
    if (mode === "range" && onSelect) {
      const rangeSelect = onSelect as (
        range: DateRange | undefined,
        selectedDate: Date,
        modifiers: Record<string, boolean>,
        e: React.MouseEvent<Element> | React.KeyboardEvent<Element>
      ) => void;

      const syntheticEvent = {
        preventDefault: () => {},
        target: document.createElement("div"),
      } as unknown as React.MouseEvent<Element>;

      const modifiers = {};

      // If no selection or not a range object, set both from and to to the clicked date
      if (!selected || typeof selected !== "object" || !("from" in selected)) {
        rangeSelect({ from: day, to: day }, day, modifiers, syntheticEvent);
      }
      // If we have a from date but no to date, or both from and to are the same date
      else if (selected.from && (!selected.to || isSameDay(selected.from, selected.to))) {
        // If clicking the same date that's already selected, keep it as single date
        if (isSameDay(day, selected.from)) {
          rangeSelect({ from: day, to: day }, day, modifiers, syntheticEvent);
        } else {
          // Create a range from the previously selected date to the new date
          if (day < selected.from) {
            rangeSelect({ from: day, to: selected.from }, day, modifiers, syntheticEvent);
          } else {
            rangeSelect({ from: selected.from, to: day }, day, modifiers, syntheticEvent);
          }
        }
      }
      // If we already have a complete range, start over with the new date
      else {
        rangeSelect({ from: day, to: day }, day, modifiers, syntheticEvent);
      }
    } else if (onSelect) {
      const dateSelect = onSelect as (
        date: Date | undefined,
        selectedDate: Date,
        modifiers: Record<string, boolean>,
        e: React.MouseEvent<Element> | React.KeyboardEvent<Element>
      ) => void;

      const syntheticEvent = {
        preventDefault: () => {},
        target: document.createElement("div"),
      } as unknown as React.MouseEvent<Element>;

      const modifiers = {};

      dateSelect(day, day, modifiers, syntheticEvent);
    }
  };

  const navigateMonth = (monthOffset: number) => {
    const newMonth = new Date(month);
    newMonth.setMonth(month.getMonth() + monthOffset);
    setMonth(newMonth);
  };

  // Check if a date is selected in single date mode
  const isSelectedDate = (date: Date) => {
    if (mode === "default" && selected instanceof Date) {
      return isSameDay(date, selected);
    }
    return false;
  };

  // Check if a date is within the selected range
  const isInSelectedRange = (date: Date) => {
    if (
      mode === "range" &&
      selected &&
      typeof selected === "object" &&
      "from" in selected &&
      selected.from &&
      selected.to
    ) {
      return isWithinInterval(date, { start: selected.from, end: selected.to });
    }
    return false;
  };

  // Check if a date is the start of the range
  const isRangeStart = (date: Date) => {
    if (mode === "range" && selected && typeof selected === "object" && "from" in selected && selected.from) {
      return isSameDay(date, selected.from);
    }
    return false;
  };

  // Check if a date is the end of the range
  const isRangeEnd = (date: Date) => {
    if (mode === "range" && selected && typeof selected === "object" && "from" in selected && selected.to) {
      return isSameDay(date, selected.to);
    }
    return false;
  };

  // Check if a date is between range start and end (not including start or end)
  const isRangeBetween = (date: Date) => {
    if (
      mode === "range" &&
      selected &&
      typeof selected === "object" &&
      "from" in selected &&
      selected.from &&
      selected.to
    ) {
      return (
        isWithinInterval(date, { start: selected.from, end: selected.to }) &&
        !isSameDay(date, selected.from) &&
        !isSameDay(date, selected.to)
      );
    }
    return false;
  };

  // Determine range position within a week
  const getRangePositionStyles = (week: Date[]) => {
    if (
      mode !== "range" ||
      !selected ||
      typeof selected !== "object" ||
      !("from" in selected) ||
      !selected.from ||
      !selected.to
    ) {
      return null;
    }

    // Find start and end index of the range within this week
    let rangeStartIndex = -1;
    let rangeEndIndex = -1;

    week.forEach((day, index) => {
      if (isRangeStart(day)) rangeStartIndex = index;
      if (isRangeEnd(day)) rangeEndIndex = index;
    });

    // Check if any day in this week is within the range
    const containsRangeDays = week.some((day) => isInSelectedRange(day));

    if (!containsRangeDays) return null;

    let left = 0;
    let right = 0;
    let width = "100%";

    if (rangeStartIndex !== -1 && rangeEndIndex !== -1) {
      left = (rangeStartIndex / 7) * 100;
      right = 100 - ((rangeEndIndex + 1) / 7) * 100;
      width = `${((rangeEndIndex - rangeStartIndex + 1) / 7) * 100}%`;
    } else if (rangeStartIndex !== -1) {
      left = (rangeStartIndex / 7) * 105;
      right = 0;
      width = `${101 - left}%`;
    } else if (rangeEndIndex !== -1) {
      left = -1;
      right = 105 - ((rangeEndIndex + 1) / 7) * 100;
      width = `${100 - right}%`;
    }

    return {
      left: `${left}%`,
      width,
      right: `${right}%`,
    };
  };

  const getFirstAvailableDate = React.useCallback(() => {
    const start = new Date(month.getFullYear(), month.getMonth() - 6, 1);
    const end = new Date(month.getFullYear(), month.getMonth() + 6, 0);

    for (let date = new Date(start); date <= end; date.setDate(date.getDate() + 1)) {
      const d = new Date(date);
      const isDisabled = disabled?.(d) ?? false;
      const isWeekend = isWeekendDisabled && (d.getDay() === 0 || d.getDay() === 6);
      if (!isDisabled && !isWeekend) {
        return d;
      }
    }

    return null;
  }, [disabled, isWeekendDisabled, month]);

  const getLastAvailableDate = React.useCallback(() => {
    const start = new Date(month.getFullYear(), month.getMonth() + 6, 0);
    const end = new Date(month.getFullYear(), month.getMonth() - 6, 1);

    for (let date = new Date(start); date >= end; date.setDate(date.getDate() - 1)) {
      const d = new Date(date);
      const isDisabled = disabled?.(d) ?? false;
      const isWeekend = isWeekendDisabled && (d.getDay() === 0 || d.getDay() === 6);
      if (!isDisabled && !isWeekend) {
        return d;
      }
    }

    return null;
  }, [disabled, isWeekendDisabled, month]);

  const minAvailableDate = getFirstAvailableDate();
  const maxAvailableDate = getLastAvailableDate();

  const isPrevMonthDisabled = React.useMemo(() => {
    if (!minAvailableDate) return true;

    const today = new Date();
    const currentMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const prevMonth = new Date(month.getFullYear(), month.getMonth() - 1, 1);

    // If previous month is before current month → disable
    const isBeforeCurrentMonth =
      prevMonth.getFullYear() < currentMonthStart.getFullYear() ||
      (prevMonth.getFullYear() === currentMonthStart.getFullYear() &&
        prevMonth.getMonth() < currentMonthStart.getMonth());

    // Or if it's before the earliest available month
    const isBeforeMinAvailable =
      prevMonth.getFullYear() < minAvailableDate.getFullYear() ||
      (prevMonth.getFullYear() === minAvailableDate.getFullYear() &&
        prevMonth.getMonth() < minAvailableDate.getMonth());

    return isBeforeCurrentMonth || isBeforeMinAvailable;
  }, [month, minAvailableDate]);

  const isNextMonthDisabled = React.useMemo(() => {
    if (!maxAvailableDate) return true;

    const nextMonth = new Date(month.getFullYear(), month.getMonth() + 1, 1);

    // Only disable if nextMonth starts after maxAvailableDate's month
    return (
      nextMonth.getFullYear() > maxAvailableDate.getFullYear() ||
      (nextMonth.getFullYear() === maxAvailableDate.getFullYear() && nextMonth.getMonth() > maxAvailableDate.getMonth())
    );
  }, [month, maxAvailableDate]);

  // localized button labels
  const resetButtonLabel = React.useMemo(() => {
    switch (locale) {
      case "English":
        return "Reset";
      case "Dutch":
        return "Resetten";
      case "French":
      default:
        return "Réinitialiser";
    }
  }, [locale]);

  const nextButtonLabel = React.useMemo(() => {
    switch (locale) {
      case "English":
        return "Confirm";
      case "Dutch":
        return "Bevestigen";
      case "French":
      default:
        return "Suivant";
    }
  }, [locale]);

  const formatDateForAria = (date: Date) => {
    return format(date, "dd MMMM yyyy", { locale: dateLocale });
  };

  const getSelectedDateRangeDescription = () => {
    if (mode === "range" && selected && typeof selected === "object" && "from" in selected) {
      if (selected.from && selected.to) {
        return locale === "French"
          ? `Du ${formatDateForAria(selected.from)} au ${formatDateForAria(selected.to)}`
          : locale === "Dutch"
            ? `Van ${formatDateForAria(selected.from)} tot ${formatDateForAria(selected.to)}`
            : `From ${formatDateForAria(selected.from)} to ${formatDateForAria(selected.to)}`;
      } else if (selected.from) {
        return locale === "French"
          ? `À partir du ${formatDateForAria(selected.from)}`
          : locale === "Dutch"
            ? `Vanaf ${formatDateForAria(selected.from)}`
            : `From ${formatDateForAria(selected.from)}`;
      }
    } else if (selected instanceof Date) {
      return formatDateForAria(selected);
    }
    return locale === "French"
      ? "Aucune date sélectionnée"
      : locale === "Dutch"
        ? "Geen datum geselecteerd"
        : "No date selected";
  };

  const currentMonthLabel = format(month, "MMMM yyyy", { locale: dateLocale });

  const nextMonthAriaLabel = format(new Date(month.getFullYear(), month.getMonth() + 1, 1), "MMMM yyyy", {
    locale: dateLocale,
  });

  const prevMonthAriaLabel = format(new Date(month.getFullYear(), month.getMonth() - 1, 1), "MMMM yyyy", {
    locale: dateLocale,
  });

  const nextMonthLabel =
    locale === "French"
      ? `Mois suivant: ${nextMonthAriaLabel}`
      : locale === "Dutch"
        ? `Volgende maand: ${nextMonthAriaLabel}`
        : `Next month: ${nextMonthAriaLabel}`;

  const prevMonthLabel =
    locale === "French"
      ? `Mois précédent : ${prevMonthAriaLabel}`
      : locale === "Dutch"
        ? `Vorige maand: ${prevMonthAriaLabel}`
        : `Previous month: ${prevMonthAriaLabel}`;

  return (
    <div className="border-light text-dark-primary w-full rounded-xl border bg-white shadow-sm sm:min-w-[370px]">
      <div className="flex w-full items-center justify-between px-6">
        <h6 className="mb-4 pt-5 text-lg font-medium">{title}</h6>
        <div className="flex items-center gap-1.25">
          <Button
            aria-label={prevMonthLabel}
            disabled={isPrevMonthDisabled}
            onClick={() => navigateMonth(-1)}
            variant="secondary"
            className="size-7.5"
            size="icon"
          >
            <ChevronLeft strokeWidth={3} />
          </Button>
          <Button
            aria-label={nextMonthLabel}
            disabled={isNextMonthDisabled}
            onClick={() => navigateMonth(1)}
            variant="secondary"
            className="size-7.5"
            size="icon"
          >
            <ChevronRight strokeWidth={3} />
          </Button>
        </div>
      </div>
      <div className="sr-only" aria-live="polite">
        {getSelectedDateRangeDescription()}
      </div>

      <table className="w-full" aria-labelledby="calendar-heading" role="grid">
        <caption className="mb-3 flex items-center justify-between px-7 pt-3">
          <h3 className="font-cabinet-grotesk text-xl font-bold capitalize">{currentMonthLabel}</h3>
        </caption>

        <thead>
          <tr className="mx-2.5 grid grid-cols-7 border-b border-gray-200 px-3 pb-2">
            {daysOfWeek.map((day, index) => (
              <th key={index} scope="col" className="text-center text-xs font-medium text-gray-500">
                {day}
              </th>
            ))}
          </tr>
        </thead>

        <tbody className="block space-y-2.5 px-7 pb-5.5">
          {calendarDays.map((week, weekIndex) => {
            const rangeStyles = getRangePositionStyles(week);

            return (
              <tr key={weekIndex} className="relative flex justify-between gap-0">
                {rangeStyles && (
                  <td className="absolute inset-0 p-0" colSpan={7} style={{ padding: 0, border: 0 }}>
                    <div
                      className="absolute inset-y-0 z-0 rounded-full bg-[#1119280A]"
                      style={rangeStyles}
                      aria-hidden="true"
                    />
                  </td>
                )}

                {/* Days */}
                {week.map((day, dayIndex) => {
                  const isOutsideMonth = !isSameMonth(day, month);
                  const isSelected = isSelectedDate(day);
                  const isStart = isRangeStart(day);
                  const isEnd = isRangeEnd(day);
                  const isBetween = isRangeBetween(day);
                  const dateLabel = formatDateForAria(day);
                  const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                  const isDisabled =
                    isCalendarDateBeforeToday(day) ||
                    (isWeekendDisabled && isWeekend) ||
                    (disabled?.(day) ?? false) ||
                    isHoliday(disableHolidays, day);

                  return (
                    <td key={dayIndex} className="relative z-10 flex items-center justify-center p-0">
                      <button
                        type="button"
                        disabled={isDisabled}
                        onClick={() => handleDateClick(day)}
                        className={cn(
                          "flex size-8.5 cursor-pointer items-center justify-center rounded-full text-base font-medium transition-colors",
                          isOutsideMonth && "text-gray-300",
                          isDisabled && "cursor-not-allowed text-gray-300",
                          !isOutsideMonth &&
                            !isSelected &&
                            !isStart &&
                            !isEnd &&
                            !isBetween &&
                            !isDisabled &&
                            "text-gray-800",
                          (isSelected || isStart || isEnd) && !isDisabled && "bg-blue-brand text-white",
                          isBetween && !isDisabled && "text-blue-brand"
                        )}
                        aria-label={dateLabel}
                        aria-current={isSameDay(day, new Date()) ? "date" : undefined}
                        tabIndex={isOutsideMonth || isDisabled ? -1 : 0}
                      >
                        {format(day, "d")}
                      </button>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="flex items-center justify-between border-t border-gray-200 p-3">
        <button
          onClick={handleReset}
          className="focus:outline-dark-primary/30 ml-1 cursor-pointer rounded-md p-2 font-medium underline underline-offset-2 sm:ml-3 md:ml-4"
          aria-label={resetButtonLabel}
        >
          {resetButtonLabel}
        </button>
        <Button onClick={() => onClose?.()} size="md" className="h-11">
          {nextButtonLabel}
        </Button>
      </div>
    </div>
  );
}

export { Calendar };
